<template>
  <div class="flex flex-col justify-between flex-grow h-full min-w-0 m-0">
    <transition name="slide-up">
      <div :class="{ 'min-h-[4rem] flex': shouldShowSpinner }">
        <span v-if="shouldShowSpinner" class="spinner message" />
      </div>
    </transition>
    <ul class="conversation-panel custom-scroll gap-3">
      <template v-for="(item, index) in messagesWithDateSeparators">
        <li
          v-if="item.type === 'separator'"
          :key="`sep-${index}`"
          class="flex flex-row items-center gap-3 my-3 px-4"
        >
          <div class="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
          <span
            class="text-xs text-gray-800 px-2.5 py-1.5 bg-white rounded-lg whitespace-nowrap"
          >
            {{ item.date }}
          </span>
          <div class="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
        </li>
        <message
          v-else-if="item.type === 'message'"
          :key="`msg-${item.data.id}`"
          :class="[
            item.isRead ? 'message--read' : 'message--unread',
            'ph-no-capture',
            { wa: isSomeOfOnlineWaInbox },
          ]"
          data-clarity-mask="True"
          :data="item.data"
          :is-a-tweet="isATweet"
          :is-a-tiktok="isATiktokChannel"
          :deleteable="!isSomeOfOnlineWaInbox || Boolean(item.data.delay_hours)"
          :assignable="isAssignable"
          :is-a-whats-app-channel="isAWhatsAppChannel"
          :has-instagram-story="hasInstagramStory"
          :is-web-widget-inbox="isAWebWidgetInbox"
          :inbox-supports-reply-to="inboxSupportsReplyTo"
          :in-reply-to="getInReplyToMessage(item.data)"
          :d99d-features="d99dFeatures"
          :private-notes="item.privateNotes"
          @forward="handleForward"
        />
      </template>
    </ul>
    <conversation-label-suggestion
      v-if="shouldShowLabelSuggestions"
      :suggested-labels="labelSuggestions"
      :chat-labels="currentChat.labels"
      :conversation-id="currentChat.id"
    />

    <div
      class="conversation-footer"
      :class="{ 'modal-mask': isPopoutReplyBox }"
    >
      <div v-if="isAnyoneTyping" class="typing-indicator-wrap">
        <div class="typing-indicator">
          {{ typingUserNames }}
          <img
            class="gif"
            src="~dashboard/assets/images/typing.gif"
            alt="Someone is typing"
          />
        </div>
      </div>
    </div>
    <forward-email-modal
      v-if="showForwardModal"
      :show-forward-modal="showForwardModal"
      :forward-message-data="forwardMessageData"
      :inbox="inbox"
      :on-forward-cancel="onForwardCancel"
      :on-forward-sent="onForwardSent"
    />
  </div>
</template>

<script>
import Message from './EmailMessage.vue';
import ConversationLabelSuggestion from '../../../../components/widgets/conversation/conversation/LabelSuggestion.vue';
import ForwardEmailModal from './ForwardEmailModal.vue';

import { mapGetters } from 'vuex';
import { computed } from 'vue';

import conversationMixin, {
  filterDuplicateSourceMessages,
} from '../../../../mixins/conversations';
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';
import timeMixin from '../../../../mixins/time';
import configMixin from 'shared/mixins/configMixin';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import aiMixin from 'dashboard/mixins/aiMixin';

import { getTypingUsersText } from '../../../../helper/commons';
import { calculateScrollTop } from '../../../../components/widgets/conversation/helpers/scrollTopCalculationHelper';
import { isEscape } from 'shared/helpers/KeyboardHelpers';
import { LocalStorage } from 'shared/helpers/localStorage';

import { MESSAGE_TYPE } from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { REPLY_POLICY } from 'shared/constants/links';
import wootConstants from 'dashboard/constants/globals';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

export default {
  name: 'EmailMessagesView',
  components: {
    Message,
    ConversationLabelSuggestion,
    ForwardEmailModal,
  },
  mixins: [
    conversationMixin,
    inboxMixin,
    eventListenerMixins,
    configMixin,
    aiMixin,
    timeMixin,
  ],
  provide() {
    return {
      latestIncomingMessageId: computed(() => this.latestIncomingMessageId),
      lastNonActivityMessageId: computed(() => this.lastNonActivityMessageId),
    };
  },
  data() {
    return {
      isLoadingPrevious: false,
      heightBeforeLoad: null,
      conversationPanel: null,
      hasUserScrolled: false,
      isProgrammaticScroll: false,
      isPopoutReplyBox: false,
      online99Fb2PrivateReplyMessage: undefined, // d99d
      messageSentSinceOpened: false,
      labelSuggestions: [],
      showForwardModal: false,
      forwardMessageData: null,
      latestIncomingMessageId: 0,
      lastNonActivityMessageId: 0,
    };
  },

  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentChat: 'getSelectedChat',
      allConversations: 'getAllConversations',
      inboxesList: 'inboxes/getInboxes',
      listLoadingStatus: 'getAllMessagesLoaded',
      getUnreadCount: 'getUnreadCount',
      loadingChatList: 'getChatListLoadingStatus',
      appIntegrations: 'integrations/getAppIntegrations',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      currentAccountId: 'getCurrentAccountId',
      getBeforeMessages: 'getBeforeMessages',
      currentUser: 'getCurrentUser',
    }),
    isOpen() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.OPEN;
    },
    shouldShowLabelSuggestions() {
      return (
        this.isOpen &&
        this.isEnterprise &&
        this.isAIIntegrationEnabled &&
        !this.messageSentSinceOpened
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    typingUsersList() {
      const userList = this.$store.getters[
        'conversationTypingStatus/getUserList'
      ](this.currentChat.id);
      return userList;
    },
    isAnyoneTyping() {
      const userList = this.typingUsersList.filter(
        i => i.id !== this.currentUser.id
      );
      return userList.length !== 0;
    },
    typingUserNames() {
      const userList = this.typingUsersList;

      if (this.isAnyoneTyping) {
        const userListAsName = getTypingUsersText(userList);
        return userListAsName;
      }

      return '';
    },
    getMessages() {
      const messages = this.getBeforeMessages || [];
      if (this.isAWhatsAppChannel) {
        return filterDuplicateSourceMessages(messages);
      }
      return messages;
    },
    getReadMessages() {
      return this.readMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    getUnReadMessages() {
      return this.unReadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    shouldShowSpinner() {
      return (
        (this.currentChat && this.currentChat.dataFetched === undefined) ||
        (!this.listLoadingStatus && this.isLoadingPrevious)
      );
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },

    isATweet() {
      return this.conversationType === 'tweet';
    },

    hasInstagramStory() {
      return this.conversationType === 'instagram_direct_message';
    },
    getLastSeenAt() {
      const { contact_last_seen_at: contactLastSeenAt } = this.currentChat;
      return contactLastSeenAt;
    },
    // d99d
    isAssignable() {
      return window.online99Constants.platform_SendFileFeature;
    },
    d99dFeatures() {
      return {
        fb2Featureable:
          this.isAFacebook2Inbox &&
          this.currentChat.additional_attributes.type !== 'instagram_comment',
        handlePrivateReply: message =>
          (this.online99Fb2PrivateReplyMessage = message),
      };
    },
    // .
    replyWindowBannerMessage() {
      if (this.isAWhatsAppChannel) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_CAN_REPLY');
      }
      if (this.isAPIInbox) {
        const { additional_attributes: additionalAttributes = {} } = this.inbox;
        if (additionalAttributes) {
          const {
            agent_reply_time_window_message: agentReplyTimeWindowMessage,
          } = additionalAttributes;
          return agentReplyTimeWindowMessage;
        }
        return '';
      }
      return this.$t('CONVERSATION.CANNOT_REPLY');
    },
    replyWindowLink() {
      if (this.isAWhatsAppChannel) {
        return REPLY_POLICY.FACEBOOK;
      }
      if (!this.isAPIInbox) {
        return REPLY_POLICY.TWILIO_WHATSAPP;
      }
      return '';
    },
    replyWindowLinkText() {
      if (this.isAWhatsAppChannel) {
        return this.$t('CONVERSATION.24_HOURS_WINDOW');
      }
      if (!this.isAPIInbox) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_24_HOURS_WINDOW');
      }
      return '';
    },
    messagesWithDateSeparators() {
      const result = [];
      const seenIds = new Set();
      let lastDate = null;

      const addMessage = (message, isRead) => {
        if (seenIds.has(message.id)) return;
        seenIds.add(message.id);
        if (message.message_type === MESSAGE_TYPE.INCOMING) {
          this.latestIncomingMessageId = message.id;
        }
        if (
          message.message_type !== MESSAGE_TYPE.ACTIVITY &&
          !message.private
        ) {
          this.lastNonActivityMessageId = message.id;
        }
        if (message.private && message.content_attributes?.in_reply_to) {
          const r = result.find(
            m => m.id === Number(message.content_attributes.in_reply_to)
          );
          if (r) r.privateNotes.push(message);
        } else {
          const dateLabel = this.relativeDate(message.created_at);
          if (dateLabel !== lastDate) {
            result.push({ type: 'separator', date: dateLabel });
            lastDate = dateLabel;
          }

          result.push({
            id: message.id,
            type: 'message',
            data: message,
            privateNotes: [],
            isRead,
          });
        }
      };

      this.getReadMessages.forEach(message => addMessage(message, true));
      this.getUnReadMessages.forEach(message => addMessage(message, false));
      return result;
    },
    unreadMessageCount() {
      return this.currentChat.unread_count || 0;
    },
    inboxSupportsReplyTo() {
      return (
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO) &&
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.MESSAGE_REPLY_TO
        ) &&
        this.currentChat.can_reply // d99d
      );
    },
    currentChatCanReply() {
      return this.currentChat.can_reply;
    },
  },

  watch: {
    currentChat(newChat, oldChat) {
      if (newChat.id === oldChat.id) {
        return;
      }
      this.fetchAllAttachmentsFromCurrentChat();
      this.fetchSuggestions();
      this.messageSentSinceOpened = false;
    },
    currentChatCanReply: {
      handler(newVal) {
        if (newVal === undefined) {
          this.fetchSingleConversation();
        }
      },
      immediate: true,
    },
  },

  created() {
    bus.$on(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    // when a new message comes in, we refetch the label suggestions
    bus.$on(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS, this.fetchSuggestions);
    // when a message is sent we set the flag to true this hides the label suggestions,
    // until the chat is changed and the flag is reset in the watch for currentChat
    bus.$on(BUS_EVENTS.MESSAGE_SENT, () => {
      this.messageSentSinceOpened = true;
    });
  },

  mounted() {
    this.addScrollListener();
    this.fetchAllAttachmentsFromCurrentChat();
    this.fetchSuggestions();
  },

  beforeDestroy() {
    this.removeBusListeners();
    this.removeScrollListener();
  },

  methods: {
    async fetchSuggestions() {
      return; // d99d disable it at all
      // start empty, this ensures that the label suggestions are not shown
      this.labelSuggestions = [];

      if (this.isLabelSuggestionDismissed()) {
        return;
      }

      if (!this.isEnterprise) {
        return;
      }

      // method available in mixin, need to ensure that integrations are present
      await this.fetchIntegrationsIfRequired();

      if (!this.isAIIntegrationEnabled) {
        return;
      }

      this.labelSuggestions = await this.fetchLabelSuggestions({
        conversationId: this.currentChat.id,
      });

      // once the labels are fetched, we need to scroll to bottom
      // but we need to wait for the DOM to be updated
      // so we use the nextTick method
      this.$nextTick(() => {
        // this param is added to route, telling the UI to navigate to the message
        // it is triggered by the SCROLL_TO_MESSAGE method
        // see setActiveChat on ConversationView.vue for more info
        const { messageId } = this.$route.query;

        // only trigger the scroll to bottom if the user has not scrolled
        // and there's no active messageId that is selected in view
        if (!messageId && !this.hasUserScrolled) {
          this.scrollToBottom();
        }
      });
    },
    isLabelSuggestionDismissed() {
      return LocalStorage.getFlag(
        LOCAL_STORAGE_KEYS.DISMISSED_LABEL_SUGGESTIONS,
        this.currentAccountId,
        this.currentChat.id
      );
    },
    fetchAllAttachmentsFromCurrentChat() {
      if (this.currentChat.id)
        this.$store.dispatch('fetchAllAttachments', this.currentChat.id); // d99d added condition, because on the mount the chat can be empty
    },
    removeBusListeners() {
      bus.$off(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    },
    onScrollToMessage({ messageId = '' } = {}) {
      this.$nextTick(() => {
        const messageElement = document.getElementById('message' + messageId);
        if (messageElement) {
          this.isProgrammaticScroll = true;
          messageElement.scrollIntoView({ behavior: 'smooth' });
          // messageElement.__vue__?.setupHighlightTimer?.(messageId); // d99d (for url link and reply link)
          this.fetchPreviousMessages(this.conversationPanel.scrollTop); // d99d - added parameter (for reply, without load previous)
        } else {
          this.scrollToBottom();
        }
      });
      this.makeMessagesRead();
    },
    showPopoutReplyBox() {
      this.isPopoutReplyBox = !this.isPopoutReplyBox;
    },
    closePopoutReplyBox() {
      this.isPopoutReplyBox = false;
    },
    handleKeyEvents(e) {
      if (isEscape(e)) {
        this.closePopoutReplyBox();
      }
    },
    addScrollListener() {
      this.conversationPanel = this.$el.querySelector('.conversation-panel');
      if (!this.conversationPanel) return;
      this.setScrollParams();
      this.conversationPanel.addEventListener('scroll', this.handleScroll);
      this.$nextTick(() => this.scrollToBottom());
    },
    removeScrollListener() {
      this.conversationPanel.removeEventListener('scroll', this.handleScroll);
    },
    scrollToBottom() {
      this.selectedTweetId = undefined; // d99d
      this.isProgrammaticScroll = true;
      let relevantMessages = [];
      this.conversationPanel = this.$el.querySelector('.conversation-panel');

      // label suggestions are not part of the messages list
      // so we need to handle them separately
      let labelSuggestions =
        this.conversationPanel.querySelector('.label-suggestion');

      // if there are unread messages, scroll to the first unread message
      if (this.unreadMessageCount > 0) {
        // capturing only the unread messages
        relevantMessages =
          this.conversationPanel.querySelectorAll('.message--unread');
      } else if (labelSuggestions) {
        // when scrolling to the bottom, the label suggestions is below the last message
        // so we scroll there if there are no unread messages
        // Unread messages always take the highest priority
        relevantMessages = [labelSuggestions];
      } else {
        // if there are no unread messages or label suggestion, scroll to the last message
        relevantMessages = Array.from(
          this.conversationPanel.querySelectorAll('.message--read')
        ).slice(-1);
      }

      this.conversationPanel.scrollTop = calculateScrollTop(
        this.conversationPanel.scrollHeight,
        this.$el.scrollHeight,
        relevantMessages
      );
    },
    setScrollParams() {
      this.heightBeforeLoad = this.conversationPanel.scrollHeight;
      this.scrollTopBeforeLoad = this.conversationPanel.scrollTop;
    },

    async fetchPreviousMessages(scrollTop = 0) {
      this.setScrollParams();
      const shouldLoadMoreMessages =
        this.currentChat.dataFetched === true &&
        !this.listLoadingStatus &&
        !this.isLoadingPrevious;

      if (
        scrollTop < 100 &&
        !this.isLoadingPrevious &&
        shouldLoadMoreMessages
      ) {
        this.isLoadingPrevious = true;
        try {
          await this.$store.dispatch('fetchPreviousMessages', {
            conversationId: this.currentChat.id,
            before:
              (this.currentChat.messages.length &&
                this.currentChat.messages[0].id) ||
              undefined, // d99d
          });
          const heightDifference =
            this.conversationPanel.scrollHeight - this.heightBeforeLoad;
          this.conversationPanel.scrollTop =
            this.scrollTopBeforeLoad + heightDifference;
          this.setScrollParams();
        } catch (error) {
          // Ignore Error
        } finally {
          this.isLoadingPrevious = false;
        }
      }
    },

    handleScroll(e) {
      if (this.isProgrammaticScroll) {
        // Reset the flag
        this.isProgrammaticScroll = false;
        this.hasUserScrolled = false;
      } else {
        this.hasUserScrolled = true;
      }
      bus.$emit(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL);
      this.fetchPreviousMessages(e.target.scrollTop);
    },

    async makeMessagesRead() {
      const needToUpdateNewCounter = this.unreadMessageCount > 0;
      if (needToUpdateNewCounter) {
        await this.$store.dispatch('markMessagesRead', {
          id: this.currentChat.id,
        });
      }
    },

    async fetchSingleConversation() {
      await this.$store.dispatch('getConversation', this.currentChat.id);
    },

    getInReplyToMessage(parentMessage) {
      if (!parentMessage) return {};
      const inReplyToMessageId = parentMessage.content_attributes?.in_reply_to;
      if (!inReplyToMessageId) return {};

      return this.currentChat?.messages.find(message => {
        if (message.id === Number(inReplyToMessageId)) {
          return true;
        }
        return false;
      });
    },
    handleForward(message) {
      if (!message) return;
      this.forwardMessageData = message;
      this.showForwardModal = true;
    },
    onForwardCancel() {
      this.showForwardModal = false;
      this.forwardMessageData = null;
    },
    async onForwardSent() {
      this.showForwardModal = false;
      this.forwardMessageData = null;
    },
    onCancelOnline99Fb2PrivateReply() {
      this.online99Fb2PrivateReplyMessage = undefined;
    },
    // .
  },
};
</script>

<style scoped>
@tailwind components;

@layer components {
  .rounded-bl-calc {
    border-bottom-left-radius: calc(1.5rem + 1px);
  }

  .rounded-tl-calc {
    border-top-left-radius: calc(1.5rem + 1px);
  }
}
</style>

<style scoped lang="scss">
:dir(rtl) {
  .ProseMirror-woot-style ul,
  .ProseMirror-woot-style ol {
    padding-left: 0;
    padding-right: 15px;
    margin-right: 0;
  }
}

.modal-mask {
  &::v-deep {
    .ProseMirror-woot-style {
      @apply max-h-[100vw-580px];

      &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }

      &::-webkit-scrollbar-track {
        background: transparent;
      }

      &::-webkit-scrollbar-thumb {
        @apply bg-[#c1c1c1] rounded-full;
        border: 2px solid transparent;
        background-clip: padding-box;
      }

      &::-webkit-scrollbar-thumb:hover {
        @apply bg-gray-400;
        border: 2px solid transparent;
        background-clip: padding-box;
      }
    }

    .reply-box {
      @apply border border-solid border-slate-75 dark:border-slate-600 max-w-[75rem] w-[70%];
    }

    .reply-box .reply-box__top {
      @apply relative min-h-[27.5rem];
    }

    .reply-box__top .input {
      @apply min-h-[27.5rem];
    }
  }

  .conversation-panel {
    > li {
      &:first-child {
        margin-top: auto;
      }
    }
  }
}

.conversation-panel {
  > li {
    @apply flex-row;
    &:first-child {
      margin-top: auto;
    }
  }

  background-image: none;
}

.reply-box {
  position: sticky;
  width: 100%;
  top: 0;
  z-index: 9;

  &::v-deep {
    .ProseMirror {
      overflow-y: auto;
      overflow-x: hidden;
      &:has(> .empty-node:only-child) {
        overflow-y: hidden;
      }
    }
  }
}
</style>
